import { FC, memo, useCallback, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { CompanyCard } from '@leadgen/models/client'
import { useSelector } from '../../../../redux/hooks'
import companySelectors from '../../../../redux/modules/company/selectors'
import useCallbackDebounced from '../../../../hooks/shared/useCallbackDebounced'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import { useCompanyCardMenu } from '../../../providers/SubscriptionProvider'

const CompanyCardMenu: FC<{
  companyCard: CompanyCard
  enableDelete?: boolean
  enableSwitch?: boolean
}> = ({ companyCard, enableDelete = false, enableSwitch = false }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false)
  const [confirmSwitchDialogOpen, setConfirmSwitchDialogOpen] = useState(false)
  const defaultStripePaymentMethodId = useSelector(companySelectors.getStripePaymentMethodId)
  const isDefaultCard = defaultStripePaymentMethodId === companyCard.id
  const { onHandleSubmitSwitchCompanyCard, onHandleSubmitDeleteCompanyCard, loading, disabled } =
    useCompanyCardMenu()

  const onMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])
  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
  }, [])
  const closeMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchorEl(null)
  }, [])
  const openConfirmSwitchCardDialog = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchorEl(null)
    setConfirmSwitchDialogOpen(true)
  }, [])
  const closeConfirmSwitchCardDialog = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchorEl(null)
    setConfirmSwitchDialogOpen(false)
  }, [])
  const openConfirmDeleteCardDialog = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchorEl(null)
    setConfirmDeleteDialogOpen(true)
  }, [])
  const closeConfirmDeleteCardDialog = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchorEl(null)
    setConfirmDeleteDialogOpen(false)
  }, [])
  const handleSubmitSwitchCompanyCard = useCallbackDebounced(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      setMenuAnchorEl(null)
      setConfirmSwitchDialogOpen(false)
      if (!disabled) {
        onHandleSubmitSwitchCompanyCard(event)
      }
    },
    [disabled, onHandleSubmitSwitchCompanyCard],
  )
  const handleSubmitDeleteCompanyCard = useCallbackDebounced(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      setMenuAnchorEl(null)
      setConfirmDeleteDialogOpen(false)
      if (!disabled) {
        onHandleSubmitDeleteCompanyCard(event)
      }
    },
    [disabled, onHandleSubmitDeleteCompanyCard],
  )
  return (
    <>
      <IconButton
        disabled={disabled}
        color="primary"
        aria-label="more options menu"
        onMouseDown={onMouseDown}
        onClick={openMenu}
        loading={loading}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        slotProps={{
          list: {
            role: 'listbox',
          },
        }}
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onMouseDown={onMouseDown}
        onClose={closeMenu}
      >
        {!isDefaultCard && enableSwitch && (
          <MenuItem
            disabled={isDefaultCard}
            onClick={openConfirmSwitchCardDialog}
            onMouseDown={onMouseDown}
          >
            Make default
          </MenuItem>
        )}
        {enableDelete && (
          <Tooltip
            title={
              isDefaultCard
                ? 'You cannot delete your default card'
                : 'Making this your default card will mean that all future subscription payments are taken from this card'
            }
          >
            <Box component={'span'} style={{ pointerEvents: 'auto' }}>
              <MenuItem
                disabled={isDefaultCard}
                onClick={openConfirmDeleteCardDialog}
                onMouseDown={onMouseDown}
              >
                Delete card
              </MenuItem>
            </Box>
          </Tooltip>
        )}
      </Menu>
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={closeConfirmDeleteCardDialog}
        onMouseDown={onMouseDown}
      >
        <DialogTitle>Delete card?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want delete this card?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDeleteCardDialog}>No</Button>
          <Button value={companyCard.id} onClick={handleSubmitDeleteCompanyCard} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmSwitchDialogOpen}
        onClose={closeConfirmSwitchCardDialog}
        onMouseDown={onMouseDown}
      >
        <DialogTitle>Make default?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to make this your default card?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmSwitchCardDialog}>No</Button>
          <Button value={companyCard.id} onClick={handleSubmitSwitchCompanyCard} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default memo(CompanyCardMenu)
